import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, shareReplay, timeout } from 'rxjs/operators';
import type { BrandId, TargetedSyncResponse } from '../xgrit-api/sync-user-response';
import { XGritApiService } from '../xgrit-api/xgrit-api.service';

/**
 * Page brands whose account brand collapses into a different `targeted-sync` `brandId` (EPS-4664).
 *
 * `targeted-sync` reports the account's owning brand in the XGrit namespace (`ACE` | `DEC` | `IDS` |
 * `AARP` | `FLVS`). AceableAgent (page `iterable.brandId = 'AA'`) has no distinct account brand — it
 * folds into `ACE` (one shared Aceable Ambassador account), so a real AceableAgent purchaser returns
 * `brandId: 'ACE'`. Without this alias the AA page compares `ACE !== AA` and hides the link even for
 * its own purchaser. Everything else already agrees (ace/ins/mtg/hsp pages are `ACE`; ids/dec match),
 * so the map only needs the AA outlier.
 */
const PAGE_BRAND_ALIASES: Record<string, BrandId> = { AA: 'ACE' };

/**
 * Normalizes a page's `iterable.brandId` into the account-brand namespace `targeted-sync` returns,
 * so the brand gate compares like-for-like. Uppercases first, then applies {@link PAGE_BRAND_ALIASES}
 * (identity for any brand not in the map). Callers compare the result against `targeted-sync`'s
 * (also-uppercased) `brandId`.
 */
export function normalizePageBrandId(pageBrandId: string | undefined): string {
  const upper = (pageBrandId ?? '').toUpperCase();
  return PAGE_BRAND_ALIASES[upper] ?? upper;
}

/** The page brand a referral link must have been minted for. See {@link PAGE_REFERRAL_SIGNATURES}. */
interface ReferralLinkSignature {
  /** Vanity share host the link is served from, lowercase, no scheme. */
  host: string;
  /** `utm_campaign` token[0] — the brand family. `A` and `AA` are DIFFERENT brands. */
  brandToken: string;
  /** `utm_campaign` token[1] — the course vertical within that brand. */
  verticalToken: string;
}

/**
 * Identifies which page each referral link belongs on, from the link itself (EPS-4664).
 *
 * A GetAmbassador referral URL names its own origin twice: the vanity host, and the first two
 * underscore-separated tokens of `utm_campaign` (`{brandToken}_{verticalToken}_{placement...}`).
 * Together those separate every brand AND every vertical — including the five that collapse to
 * `brandId: ACE` on `targeted-sync` — so no extra API field is needed to gate the link.
 *
 * Keyed on `${iterable.brandId}:${iterable.appType}`, which is unique per brand. The key uses the
 * page's RAW `brandId` (aa is `AA`, not the `ACE` alias) so this stays independent of the brand half
 * of the gate. aarp ships empty values, produces the key `':'`, matches nothing, and fails closed.
 *
 * No collisions: within `share.aceable.com`, token[0] separates `A` from `AA`, and within `A` the
 * token[1] set is `{D, I, M, H}`.
 *
 * DELIBERATELY ABSENT, not overlooked — aarp (`brandId: ''`) and qa (`brandId: 'QA'`). Neither ships a
 * referral program: neither resolves a usable group id in xcc-api's `AMBASSADOR_GROUP_ID_BY_BRAND`
 * (aarp is mapped but empty, qa is absent entirely), so
 * there is no GetAmbassador group to mint against and no link that could legitimately render. They fail closed on
 * the missing key, which is the correct outcome. Note qa's deployed envs point XGrit at
 * `brandId: 'ACE'`, so a logged-in QA build resolves an ACE account and still shows the intake box —
 * that is intended. Do NOT "fix" it by aliasing QA to ACE here: that would switch the gate on for a
 * brand with no group id, turning a correct intake box into a mint that cannot succeed.
 *
 * PROVENANCE (2026-09-15) — the rows are not equally well evidenced:
 * - OBSERVED, from a real referral URL: ace, aa, ins, ids, dec. These five are pinned verbatim in
 *   the `observed production links` block of this file's spec.
 * - INFERRED: mtg (`A_M`) and hsp (`A_H`). Their CAMPAIGN strings are confirmed, but no full URL has
 *   been captured, so `host: 'share.aceable.com'` is assumed from the pattern the other ACE-family
 *   links follow rather than seen.
 *
 * Verify the two inferred rows against a live mtg/hsp link when one is available. If either host is
 * wrong the gate fails CLOSED, which here means those brands' ambassadors get the intake box forever
 * with no error raised anywhere — a silent, permanent loss rather than a visible break.
 */
const PAGE_REFERRAL_SIGNATURES: Record<string, ReferralLinkSignature> = {
  'ACE:DE': { host: 'share.aceable.com', brandToken: 'A', verticalToken: 'D' },
  'AA:RE': { host: 'share.aceable.com', brandToken: 'AA', verticalToken: 'RE' },
  'ACE:INS': { host: 'share.aceable.com', brandToken: 'A', verticalToken: 'I' },
  'ACE:MTG': { host: 'share.aceable.com', brandToken: 'A', verticalToken: 'M' },
  'ACE:HSP': { host: 'share.aceable.com', brandToken: 'A', verticalToken: 'H' },
  'IDS:DE': { host: 'share.idrivesafely.com', brandToken: 'I', verticalToken: 'D' },
  'DEC:DE': { host: 'share.driversed.com', brandToken: 'D', verticalToken: 'D' },
};

/** Schemes a referral link may use. Anything else is rejected outright — see {@link parseReferralLink}. */
const ALLOWED_REFERRAL_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:']);

/**
 * Splits a referral URL into the two things that identify it: its host and its `utm_campaign` tokens.
 *
 * Returns null — never a partial result — for anything unusable: an empty/relative/malformed URL, a
 * non-web scheme, or one with no `utm_campaign`. Callers treat null as "cannot confirm" and fall back
 * to the intake box.
 *
 * The scheme check is load-bearing, not hygiene. `new URL()` happily parses `javascript://share.
 * aceable.com/?utm_campaign=A_D` and reports `hostname: 'share.aceable.com'` with a matching campaign,
 * so host+token comparison ALONE would return true for it and hand a `javascript:` URL to the
 * template's href and to the clipboard. `data:` behaves the same way. Both schemes are rejected here
 * rather than at the comparison, so every caller inherits the guarantee.
 */
export function parseReferralLink(url: string | undefined): { host: string; tokens: string[] } | null {
  if (!url) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (!ALLOWED_REFERRAL_PROTOCOLS.has(parsed.protocol)) {
    return null;
  }

  const campaign = parsed.searchParams.get('utm_campaign');
  if (!campaign) {
    return null;
  }

  return { host: parsed.hostname.toLowerCase(), tokens: campaign.toUpperCase().split('_') };
}

/**
 * True when `url` was minted for the page described by `iterable` (EPS-4664).
 *
 * Compares the host plus `utm_campaign` token[0] and token[1], and DELIBERATELY IGNORES everything
 * after token[1]: the tail encodes placement, not identity, and varies for one brand+vertical
 * (`A_D_S_Course_L`, `A_D_P_Course_L` and `A_D_S_Help_B` are all Aceable driving). Ignoring it is
 * also what lets a single `DE` page accept both drivers-ed and defensive-driving links — token[2]
 * (`S`/`P`) is that split, so no separate DE→[DE,DD] expansion is needed.
 *
 * Comparison is POSITIONAL AND EXACT. Do not reach for `startsWith` on the raw campaign string:
 * `'AA_RE_S_Course_L'.startsWith('A')` is true, which would render an AceableAgent link on Aceable —
 * the exact bug this gate exists to prevent. Position matters for the same reason `I` is ambiguous
 * on its own: `A_I` is Insurance, `I_D` is I Drive Safely.
 *
 * Fails CLOSED on anything unrecognised — unknown page, unparseable link, unknown host or token pair.
 * A newly-added GetAmbassador campaign should cost a user their link, never leak another brand's.
 */
export function linkSignatureMatchesPage(
  url: string | undefined,
  iterable: { brandId?: string; appType?: string } | undefined,
): boolean {
  const key = `${(iterable?.brandId ?? '').toUpperCase()}:${(iterable?.appType ?? '').toUpperCase()}`;
  const signature = PAGE_REFERRAL_SIGNATURES[key];
  if (!signature) {
    return false;
  }

  const link = parseReferralLink(url);
  if (!link || link.tokens.length < 2) {
    return false;
  }

  return (
    link.host === signature.host &&
    link.tokens[0] === signature.brandToken &&
    link.tokens[1] === signature.verticalToken
  );
}

/**
 * Resolves the logged-in user's owning brand from the XGrit `user/targeted-sync` endpoint (EPS-4664).
 *
 * Lives on its own so the brand-gate feature can be introduced without editing the (heavily
 * legacy-typed) {@link XGritApiService}. It reuses that service's already-resolved `apiConfig`
 * (same host + `aceauth` header) and sends the shared session cookie (`withCredentials: true`),
 * with no query params.
 *
 * Callers compare `brandId` (UPPERCASE, TOP-LEVEL — the response is flat, there is no `data` wrapper)
 * against the page brand to decide whether a brand-scoped
 * referral link belongs on the current page. Fails CLOSED: any error — a 401 (logged out) or
 * otherwise — resolves to an empty object, which is a valid {@link TargetedSyncResponse} (every
 * field is optional), so callers fall back to the email intake box when `brandId` is absent.
 */
@Injectable({
  providedIn: 'root',
})
export class TargetedSyncService {
  private readonly httpClient = inject(HttpClient);
  private readonly xgritApi = inject(XGritApiService);

  public getTargetedSyncUser(): Observable<TargetedSyncResponse> {
    return this.httpClient
      .get<TargetedSyncResponse>(this.xgritApi.apiConfig.url + 'user/targeted-sync', {
        headers: {
          Authorization: this.xgritApi.apiConfig.aceauth,
        },
        withCredentials: true,
      })
      .pipe(
        // Bounded so a request that never settles cannot strand a caller. The RAF container starts on
        // its 'loading' screen and only leaves it when this emits, so without a timeout a hung request
        // shows a permanent spinner instead of reaching the intended fail-closed intake box. 5s matches
        // the existing XGrit timeout (`xgrit-api.service.ts`); the timeout error falls into catchError
        // below and becomes the same empty response any other failure produces.
        timeout(5000),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(() => of<TargetedSyncResponse>({})),
      );
  }
}
